import { useCallback, useMemo, useState } from 'react'

import SortOrder from '~/client/src/shared/enums/SortOrder'
import { resolveResourceValue } from '~/client/src/shared/resources/resolveResourceValue'

import { IColumn, TableGroupSortProps, UseTableGroupSortParams } from './types'

interface UseTableGroupSortResult {
  groupSortProps: TableGroupSortProps
  currentGrouping: string | null
  currentSorting: { columnKey: string | null; order: SortOrder }
  applySorting: <T>(records: T[], columns: IColumn[]) => T[]
  setCurrentGrouping: React.Dispatch<React.SetStateAction<string | null>>
}

export const useTableGroupSort = ({
  groupingOptions = [],
  sortingOptions = [],
  initialGroupByKey = null,
  initialSortKey = null,
  initialSortOrder = SortOrder.DESC,
  resourceTypeId,
  state,
  sortRecordsByKey,
}: UseTableGroupSortParams): UseTableGroupSortResult => {
  const [selectedGroupByKey, setGroupByKey] = useState<string | null>(
    initialGroupByKey,
  )
  const [selectedSortingOptionId, setSortingOptionId] = useState<string | null>(
    initialSortKey,
  )
  const [selectedSortOrder, setSortOrder] =
    useState<SortOrder>(initialSortOrder)
  const groupByKey = groupingOptions.some(
    option => option.key === selectedGroupByKey,
  )
    ? selectedGroupByKey
    : null
  const sortingOptionId =
    selectedSortingOptionId === null ||
    sortingOptions.some(option => option.key === selectedSortingOptionId)
      ? selectedSortingOptionId
      : sortingOptions.some(option => option.key === initialSortKey)
        ? initialSortKey
        : null
  const sortOrder =
    sortingOptionId === selectedSortingOptionId
      ? selectedSortOrder
      : initialSortOrder

  const handleGroupingChange = useCallback((newGroupByKey: string | null) => {
    setGroupByKey(newGroupByKey)
  }, [])

  const handleSortingChange = useCallback(
    (optionId: string, order: SortOrder) => {
      setSortingOptionId(optionId)
      setSortOrder(order)
    },
    [],
  )

  const applySorting = useCallback(
    <T>(records: T[], columns: IColumn[]): T[] => {
      if (!sortingOptionId || sortOrder === SortOrder.DEFAULT) {
        return records
      }

      const column = columns.find(c => c.dataKey === sortingOptionId)
      const resolvedFieldKey = sortingOptionId.startsWith('data.')
        ? sortingOptionId.replace('data.', '')
        : sortingOptionId

      if (sortRecordsByKey) {
        return sortRecordsByKey(records, resolvedFieldKey, sortOrder)
      }

      if (!column) return records

      const getter = column.accessorFn
      const getValue = (row: any) => {
        if (getter) {
          return getter(row)
        }

        if (sortingOptionId === 'name') {
          return row.name
        }

        if (sortingOptionId.startsWith('data.')) {
          return resolveResourceValue({
            record: row,
            resourceTypeId,
            key: sortingOptionId.replace('data.', ''),
          })
        }

        return row[sortingOptionId]
      }

      const isEmpty = (v: any) =>
        v == null ||
        (typeof v === 'string' && v.trim() === '') ||
        (typeof v === 'number' && Number.isNaN(v))

      const copy = [...records]
      copy.sort((a, b) => {
        const va = getValue(a)
        const vb = getValue(b)

        const aEmpty = isEmpty(va)
        const bEmpty = isEmpty(vb)
        if (aEmpty && bEmpty) return 0
        if (aEmpty) return 1
        if (bEmpty) return -1

        let cmp: number
        if (typeof va === 'number' && typeof vb === 'number') {
          cmp = va - vb
        } else {
          const sa = String(va).toLowerCase()
          const sb = String(vb).toLowerCase()
          cmp = sa.localeCompare(sb)
        }

        return sortOrder === SortOrder.ASC ? cmp : -cmp
      })

      return copy
    },
    [sortRecordsByKey, sortingOptionId, sortOrder, resourceTypeId],
  )

  const groupSortProps = useMemo(
    () => ({
      groupingOptions,
      sortingOptions,
      initialGroupByKey,
      initialSortKey,
      initialSortOrder,
      selectedGroupByKey: groupByKey,
      selectedSortingOptionId: sortingOptionId,
      selectedSortOrder: sortOrder,
      onGroupingChange: handleGroupingChange,
      onSortingChange: handleSortingChange,
      state,
    }),
    [
      groupingOptions,
      sortingOptions,
      initialGroupByKey,
      initialSortKey,
      initialSortOrder,
      groupByKey,
      sortingOptionId,
      sortOrder,
      handleGroupingChange,
      handleSortingChange,
      state,
    ],
  )

  const currentSorting = useMemo(
    () => ({
      columnKey: sortingOptionId,
      order: sortOrder,
    }),
    [sortingOptionId, sortOrder],
  )

  return {
    groupSortProps,
    currentGrouping: groupByKey,
    currentSorting,
    applySorting,
    setCurrentGrouping: setGroupByKey,
  }
}
